import React, { useState } from "react";

import { Error } from "@mui/icons-material";
import { Box, Grid, TextField, Typography, useTheme } from "@mui/material";

interface DeleteOrganisationProps {
  orgName: string;
  numMembers: number;
  numInactiveMembers: number;
  handleDisableOrgDeletion: (isDisabled: boolean) => void;
  handleDeletionRequestChanged: (orgDeletionRequest: string) => void;
}
const DeleteOrganisation = ({
  orgName,
  numInactiveMembers,
  numMembers,
  handleDisableOrgDeletion,
  handleDeletionRequestChanged,
}: DeleteOrganisationProps) => {
  const theme = useTheme();

  const [inputOrgName, setInputOrgName] = useState("");
  const [inputTotalUsers, setInputTotalUsers] = useState<number | undefined>(
    undefined,
  );
  const [orgDeletionRequestReference, setOrgDeletionRequestReference] =
    useState("");

  const totalMembers = numInactiveMembers + numMembers;

  return (
    <>
      <Grid container direction="row" spacing={1} style={{ minWidth: 350 }}>
        <Grid item xs={12} container direction="column">
          <Box
            bgcolor={theme.palette.warning.main}
            borderRadius="5px"
            border={1}
            borderColor={theme.palette.secondary.main}
            marginBottom={3}
          >
            <Grid container alignItems="center">
              <Grid item xs={2} container justifyContent="center">
                <Error fontSize="large" />
              </Grid>
              <Grid item xs={10} style={{ paddingRight: "20px" }}>
                <p>
                  You are about to <b>permanently delete</b> this organisation
                  with{" "}
                  <span data-testid="total-users-span">{totalMembers}</span>{" "}
                  total {totalMembers == 1 ? "user" : "users"} ({numMembers}{" "}
                  active and {numInactiveMembers} deactivated).
                </p>
                <p>
                  There are currently <b>no</b> mechanisms to recover an
                  organisation once this process has completed.
                </p>
                <p>
                  You need to enter the organisation name and total number of
                  users to confirm deletion.
                </p>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Typography variant="body1">
              Provide the link to the customer request
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="customerDeleteRequest-input"
            label="Link to customer request"
            value={orgDeletionRequestReference}
            onChange={(event) => {
              setOrgDeletionRequestReference(event.target.value.trim());
              handleDeletionRequestChanged(event.target.value.trim());
              handleDisableOrgDeletion(
                inputOrgName !== orgName ||
                  event.target.value.length === 0 ||
                  inputTotalUsers != totalMembers,
              );
            }}
            error={orgDeletionRequestReference.length === 0}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              "data-anchor": "do-customerDeleteRequest-input",
              "data-testid": "do-customerDeleteRequest-input",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Typography variant="body1">
              Type the name of the organisation to confirm the deletion
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="orgName-input"
            label="Organisation Name"
            value={inputOrgName}
            onChange={(event) => {
              setInputOrgName(event.target.value);
              handleDisableOrgDeletion(
                event.target.value !== orgName ||
                  inputTotalUsers != totalMembers ||
                  orgDeletionRequestReference.length === 0,
              );
            }}
            error={inputOrgName !== orgName}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              "data-anchor": "do-name-input",
              "data-testid": "do-name-input",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Typography variant="body1">
              Type the total number of users in the organisation to confirm the
              deletion
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="orgName-input"
            label="Total organisation members (active + deactivated)"
            type="number"
            value={inputTotalUsers}
            onChange={(event) => {
              const inputValue =
                event.target.value === ""
                  ? undefined
                  : Number(event.target.value);
              setInputTotalUsers(inputValue);
              handleDisableOrgDeletion(
                inputOrgName !== orgName ||
                  inputValue != totalMembers ||
                  orgDeletionRequestReference.length === 0,
              );
            }}
            error={inputTotalUsers != totalMembers}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              "data-anchor": "do-total-user-input",
              "data-testid": "do-total-user-input",
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default DeleteOrganisation;
